/**
 * Turn an imported file into a timeline clip. Bytes go to IndexedDB (and
 * Storage when signed in). The project document only stores the ref.
 */

import type { ImageClipRef, TimelineClip, VideoClipRef } from '@canvas/shared-types';
import type { LibraryAsset } from './libraryCatalog';
import { textStyleCard } from '@/data/textStyles';
import { clientRemoveNearBackground } from '@/lib/removeBg';
import { uploadProjectImage } from '@/lib/uploadAsset';
import { uploadProjectAudio, resolveMediaBlob, uploadProjectVideo } from './videoAssetUpload';
import { putVideoAsset } from './videoAssetIdb';
import { nextClipId, TRACK } from './timelineEdit';
import { createClipRef } from './videoClip';

export async function videoTimelineClip(file: File, projectId: string, atMs: number, durationHintMs?: number): Promise<TimelineClip> {
  const imported = await uploadProjectVideo({ file, projectId, durationHintMs });
  const ref: VideoClipRef = imported.clip;
  const durationMs = Math.max(100, ref.trimEndMs - ref.trimStartMs);
  return {
    id: nextClipId('vid'),
    trackId: TRACK.visual,
    startMs: atMs,
    durationMs,
    name: file.name.replace(/\.[^.]+$/, '').slice(0, 48) || 'Video',
    kind: 'video',
    video: ref,
  };
}

export async function imageTimelineClip(file: File, projectId: string, atMs: number): Promise<TimelineClip> {
  const uploaded = await uploadProjectImage({ file, projectId });
  const idbKey = nextClipId('img');
  await putVideoAsset(idbKey, file);
  const bitmap = await createImageBitmap(file);
  const width = bitmap.width;
  const height = bitmap.height;
  bitmap.close();
  const image: ImageClipRef = {
    v: 1,
    source: uploaded.path ? 'storage' : 'idb',
    storagePath: uploaded.path ?? undefined,
    url: uploaded.source === 'local' ? undefined : uploaded.url,
    idbKey,
    mimeType: file.type || 'image/jpeg',
    width,
    height,
    fit: 'contain',
  };
  return {
    id: nextClipId('still'),
    trackId: TRACK.visual,
    startMs: atMs,
    durationMs: 5000,
    name: file.name.replace(/\.[^.]+$/, '').slice(0, 48) || 'Image',
    kind: 'image',
    placement: { x: 0.5, y: 0.5, width: 0.72, rotation: 0, flipX: false, flipY: false, lockAspect: true },
    image,
  };
}

export async function audioTimelineClip(
  file: File,
  projectId: string,
  atMs: number,
  trackId = TRACK.audioA,
  durationHintMs?: number
): Promise<TimelineClip> {
  const track = await uploadProjectAudio({ file, projectId, durationHintMs });
  const durationMs = Math.max(100, track.trimEndMs - track.trimStartMs);
  return {
    id: nextClipId('aud'),
    trackId,
    startMs: atMs,
    durationMs,
    name: track.name || 'Audio',
    kind: 'audio',
    audio: {
      v: 1,
      source: track.source,
      storagePath: track.storagePath,
      url: track.url,
      idbKey: track.idbKey,
      mimeType: track.mimeType,
      durationMs: track.durationMs,
      trimStartMs: track.trimStartMs,
      trimEndMs: track.trimEndMs,
      volume: track.volume,
      fade: track.fade,
    },
  };
}

/** Punch the backdrop out of a photo and store the PNG in IndexedDB. */
export async function cutoutImageRef(image: ImageClipRef): Promise<ImageClipRef> {
  const blob = await resolveMediaBlob(image);
  if (!blob) throw new Error('That photo is not on this device.');
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser can’t edit that photo.');
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  const url = clientRemoveNearBackground(canvas);
  const png = await (await fetch(url)).blob();
  const idbKey = nextClipId('cutout');
  await putVideoAsset(idbKey, png);
  return {
    ...image,
    source: 'idb',
    idbKey,
    storagePath: undefined,
    url: undefined,
    mimeType: 'image/png',
    width: canvas.width,
    height: canvas.height,
  };
}

export function textClipFromLook(lookId: string, atMs: number, sample?: string): TimelineClip {
  const card = textStyleCard(lookId);
  const text = sample || card.sample;
  return {
    id: nextClipId('title'),
    trackId: TRACK.textA,
    startMs: atMs,
    durationMs: card.id === 'typewriter' ? 4000 : 3000,
    name: text.slice(0, 32) || 'Text',
    kind: 'text',
    text: {
      ...card.style,
      text,
      align: 'center',
      x: 0.5,
      y: 0.5,
      outPreset: 'fade-out',
    },
  };
}

export function textTimelineClip(text: string, atMs: number, fill = '#ffffff', fontFamily = 'Outfit, Arial, sans-serif'): TimelineClip {
  return {
    id: nextClipId('title'),
    trackId: TRACK.textA,
    startMs: atMs,
    durationMs: 3000,
    name: text.slice(0, 32) || 'Text',
    kind: 'text',
    text: {
      text: text || 'Your text',
      fontFamily,
      fontSize: 64,
      fontWeight: 700,
      fill,
      align: 'center',
      x: 0.5,
      y: 0.5,
      inPreset: 'rise',
      outPreset: 'fade-out',
    },
  };
}

function audioDurationMs(url: string): Promise<number> {
  return new Promise((resolve, reject) => {
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      const ms = Number.isFinite(audio.duration) ? Math.round(audio.duration * 1000) : 0;
      audio.src = '';
      if (ms > 0) resolve(ms);
      else reject(new Error('Could not read that sound.'));
    };
    audio.onerror = () => reject(new Error('Could not read that sound.'));
    audio.src = url;
  });
}

/** A library photo or sticker. The project stores the `/library` URL, not the bytes. */
export async function libraryImageClip(item: LibraryAsset, atMs: number): Promise<TimelineClip> {
  const sticker = item.section === 'sticker';
  let width = sticker ? 512 : 1920;
  let height = sticker ? 512 : 1080;
  try {
    const response = await fetch(item.url);
    if (response.ok) {
      const bitmap = await createImageBitmap(await response.blob());
      width = bitmap.width || width;
      height = bitmap.height || height;
      bitmap.close();
    }
  } catch {
    /* The monitor measures the file again when it paints. */
  }
  const image: ImageClipRef = {
    v: 1,
    source: 'remote',
    url: item.url,
    mimeType: item.mimeType,
    width,
    height,
    fit: sticker ? 'contain' : 'cover',
  };
  return {
    id: nextClipId(sticker ? 'sticker' : 'bg'),
    trackId: TRACK.visual,
    startMs: atMs,
    durationMs: 5000,
    name: item.title.slice(0, 48),
    kind: 'image',
    placement: sticker
      ? { x: 0.5, y: 0.45, width: 0.34, rotation: 0, flipX: false, flipY: false, lockAspect: true }
      : undefined,
    image,
  };
}

function probeVideoUrl(url: string): Promise<{ durationMs: number; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () => {
      const durationMs = Number.isFinite(video.duration) ? Math.round(video.duration * 1000) : 0;
      const width = video.videoWidth || 1280;
      const height = video.videoHeight || 720;
      video.src = '';
      if (durationMs > 0) resolve({ durationMs, width, height });
      else reject(new Error('Could not read that video.'));
    };
    video.onerror = () => reject(new Error('Could not read that video.'));
    video.src = url;
  });
}

/** A library video. The project stores the `/library` URL, not the bytes. */
export async function libraryVideoClip(item: LibraryAsset, atMs: number): Promise<TimelineClip> {
  const probe = await probeVideoUrl(item.url);
  const ref = createClipRef({
    clipId: nextClipId('libvid'),
    source: 'remote',
    mimeType: item.mimeType,
    naturalWidth: probe.width,
    naturalHeight: probe.height,
    durationMs: probe.durationMs,
    url: item.url,
  });
  return {
    id: ref.clipId,
    trackId: TRACK.visual,
    startMs: atMs,
    durationMs: probe.durationMs,
    name: item.title.slice(0, 48),
    kind: 'video',
    video: ref,
  };
}

/** A library sound, measured from the file so the clip matches its length. */
export async function libraryAudioClip(item: LibraryAsset, atMs: number): Promise<TimelineClip> {
  const durationMs = Math.max(100, await audioDurationMs(item.url));
  return {
    id: nextClipId('libaud'),
    trackId: TRACK.audioA,
    startMs: atMs,
    durationMs,
    name: item.title.slice(0, 48),
    kind: 'audio',
    audio: {
      v: 1,
      source: 'remote',
      url: item.url,
      mimeType: item.mimeType,
      durationMs,
      trimStartMs: 0,
      trimEndMs: durationMs,
      volume: 1,
      fade: { inMs: 0, outMs: 0 },
    },
  };
}

export function captionTimelineClip(text: string, atMs: number, source: 'manual' | 'auto', durationMs = 2200): TimelineClip {
  return {
    id: nextClipId('cap'),
    trackId: TRACK.caption,
    startMs: atMs,
    durationMs,
    name: text.slice(0, 32) || 'Caption',
    kind: 'caption',
    caption: { text, source },
  };
}
